import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Loader2, Mic, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, FILES_BASE } from "@/lib/api";

const STATUS_CLS = { LIVE: "text-emerald-500 border-emerald-500/30", "SETUP REQUIRED": "text-amber-700 border-amber-500/30", DISABLED: "text-slate-500 border-slate-800" };
export const StatusTag = ({ s }) => <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${STATUS_CLS[s]}`} data-testid="provider-status">{s}</span>;

export function useModels() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/models").then((r) => setM(r.data)).catch(() => setM({ providers: [] })); }, []);
  return m;
}

export function ModelWriter({ prompt, onResult }) {
  const m = useModels();
  const [sel, setSel] = useState("openai");
  const [busy, setBusy] = useState(false);
  if (!m) return null;
  const text = m.providers.filter((p) => p.caps.includes("text"));
  const cur = text.find((p) => p.provider === sel);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/write", { provider: sel, prompt }); onResult(data); toast.success(`Written by ${data.provider} (${data.credits} credits)`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="model-writer">
      <Select value={sel} onValueChange={setSel}><SelectTrigger className="w-56" data-testid="model-picker"><SelectValue /></SelectTrigger>
        <SelectContent>{text.map((p) => <SelectItem key={p.provider} value={p.provider} disabled={p.status !== "LIVE"}>{p.label} · {p.default_model}</SelectItem>)}</SelectContent></Select>
      {cur && <StatusTag s={cur.status} />}
      <Button size="sm" onClick={run} disabled={busy || !prompt || cur?.status !== "LIVE"} data-testid="model-write-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}Write with this model (~{m.text_credits} credits)</Button>
    </div>
  );
}

export function VoiceOver({ text }) {
  const m = useModels();
  const [voices, setVoices] = useState([]);
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const [audio, setAudio] = useState(null);
  const p = m?.providers.find((x) => x.provider === "elevenlabs");
  useEffect(() => { if (p?.status === "LIVE") api.get("/voice/voices").then((r) => { setVoices(r.data); setV(r.data[0]?.voice_id || ""); }).catch((e) => toast.error(errMsg(e))); }, [p?.status]);
  if (!p) return null;
  const gen = async () => { setBusy(true); try { const { data } = await api.post("/voice/tts", { text: text.slice(0, 2500), voice_id: v }); setAudio(FILES_BASE + data.file_url); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="rounded-lg border border-slate-800 p-3 space-y-2" data-testid="voiceover-panel">
      <div className="flex items-center gap-2 text-sm font-semibold"><Mic className="h-4 w-4" />Voice-over (ElevenLabs) <StatusTag s={p.status} /></div>
      {p.status !== "LIVE" ? <p className="text-xs text-slate-500" data-testid="voiceover-setup">Needs an ElevenLabs API key on the server (elevenlabs.io → Profile → API Keys). {p.billing}.</p> : <>
        <div className="flex flex-wrap gap-2"><Select value={v} onValueChange={setV}><SelectTrigger className="w-56" data-testid="voice-select"><SelectValue placeholder="Voice" /></SelectTrigger>
          <SelectContent>{voices.map((x) => <SelectItem key={x.voice_id} value={x.voice_id}>{x.name}{x.labels?.accent ? ` · ${x.labels.accent}` : ""}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" onClick={gen} disabled={busy || !v || !text} data-testid="voiceover-generate">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Create voice-over ({Math.min(text.length, 2500)} chars)</Button></div>
        <p className="text-[11px] text-slate-500">Multilingual model: speaks the script's language (Hindi, Marathi, Tamil and more). {p.billing}.</p>
        {audio && <div className="flex items-center gap-2"><audio controls src={audio} data-testid="voiceover-audio" /><a href={audio} download className="text-xs underline">Download MP3</a></div>}
      </>}
    </div>
  );
}

export function ResearchPanel({ context }) {
  const m = useModels();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const p = m?.providers.find((x) => x.provider === "perplexity");
  if (!p) return null;
  const go = async () => { setBusy(true); try { setR((await api.post("/research", { query: q, context })).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="lx-card mb-6 space-y-3" data-testid="research-panel">
      <div className="flex items-center gap-2 font-semibold"><Search className="h-4 w-4" />Research with sources (Perplexity) <StatusTag s={p.status} /></div>
      {p.status !== "LIVE" ? <p className="text-xs text-slate-500" data-testid="research-setup">Needs a Perplexity API key on the server (perplexity.ai → Settings → API). {p.billing}.</p> : <>
        <div className="flex gap-2"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={context === "competitor" ? "e.g. Launch prices of new 3BHK projects in Baner, Pune" : "e.g. Upcoming metro and infrastructure near Hinjewadi, Pune"} data-testid="research-input" />
          <Button onClick={go} disabled={busy || q.length < 5} data-testid="research-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Research"}</Button></div>
        {r && <div className="space-y-3" data-testid="research-result">
          <div className="text-[10px] font-mono text-amber-700">AI-GENERATED ANALYSIS · verify against the sources before using</div>
          <div className="prose-lx"><ReactMarkdown>{r.answer}</ReactMarkdown></div>
          <div><div className="text-xs font-semibold mb-1">Sources</div><ol className="list-decimal pl-5 text-xs space-y-0.5" data-testid="research-sources">
            {(r.search_results?.length ? r.search_results : r.citations.map((u) => ({ url: u }))).map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noreferrer" className="underline break-all">{s.title || s.url}</a>{s.date ? ` · ${s.date}` : ""}</li>)}</ol></div>
        </div>}
      </>}
    </div>
  );
}

export function ModelRegistry() {
  const [m, setM] = useState(null);
  const [usage, setUsage] = useState([]);
  const load = () => { api.get("/models").then((r) => setM(r.data)); api.get("/admin/models/usage").then((r) => setUsage(r.data)); };
  useEffect(() => { load(); }, []);
  const save = async (p, patch) => { try { await api.put("/admin/models", { provider: p.provider, enabled: p.enabled, models: p.models, default_model: p.default_model, ...patch }); toast.success("Saved"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!m) return null;
  return (
    <div className="space-y-4" data-testid="model-registry">
      <p className="text-xs text-slate-500">API keys stay on the server (backend environment) and are never shown here. ChatGPT, Claude and Gemini use the Emergent Universal Key.</p>
      {m.providers.map((p) => (
        <div key={p.provider} className="lx-card grid md:grid-cols-[1fr_auto] gap-3" data-testid={`registry-${p.provider}`}>
          <div className="space-y-2"><div className="font-semibold flex items-center gap-2">{p.label}<StatusTag s={p.status} /><span className="text-[11px] text-slate-500">{p.caps.join(", ")} · {p.billing}</span></div>
            <Textarea rows={1} defaultValue={p.models.join(", ")} onBlur={(e) => save(p, { models: e.target.value.split(",") })} data-testid={`registry-models-${p.provider}`} />
            <div className="text-[11px] text-slate-500">Default model: {p.default_model} · Key {p.configured ? "configured" : "missing"}</div></div>
          <Button variant={p.enabled ? "secondary" : "default"} onClick={() => save(p, { enabled: !p.enabled })} data-testid={`registry-toggle-${p.provider}`}>{p.enabled ? "Disable" : "Enable"}</Button>
        </div>))}
      <div className="lx-card"><div className="font-semibold mb-2">Usage</div><table className="w-full text-xs" data-testid="registry-usage"><tbody>
        {usage.map((u) => <tr key={u.provider + u.model} className="border-t border-slate-800"><td className="py-1">{u.provider}</td><td>{u.model}</td><td>{u.calls} calls</td><td>{u.credits} credits</td><td>{u.units} chars/tokens</td></tr>)}
        {!usage.length && <tr><td className="py-2 text-slate-500">No usage yet</td></tr>}</tbody></table></div>
    </div>
  );
}
